import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Loader2 } from 'lucide-react';

const ViewInvoiceRedirect = () => {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [userType, setUserType] = useState(null); // 'receevit' or 'guest' or null
  const [error, setError] = useState(null);

  const seo = (
    <Helmet>
      <title>View Invoice | Receevit®</title>
      <meta name="description" content="View your Receevit® invoice securely. Log in or use guest lookup to access invoice details." />
      <link rel="canonical" href="https://www.receevit.com/view-invoice" />
      <meta property="og:title" content="View Invoice | Receevit®" />
      <meta property="og:description" content="View your Receevit® invoice securely. Log in or use guest lookup to access invoice details." />
      <meta property="og:url" content={`https://www.receevit.com/view-invoice${invoiceId ? `/${invoiceId}` : ''}`} />
      <meta property="og:type" content="website" />
      <meta property="og:image" content="https://www.receevit.com/og-image.png" />
    </Helmet>
  );

  useEffect(() => {
    const checkRedirect = async () => {
      try {
        setLoading(true);
        
        // Call the redirect check endpoint
        const response = await api.get(`/invoices/view-redirect/${invoiceId}`);
        
        if (response.data.success) {
          const { isReceevitUser, redirectUrl } = response.data.data;
          
          // If Receevit® user, check if they're logged in
          if (isReceevitUser) {
            setUserType('receevit');
            // Check if user is logged in
            const token = localStorage.getItem('token');
            if (token) {
              // User is logged in, go to received invoices page with invoice highlighted
              navigate(redirectUrl, { replace: true });
            } else {
              // User is not logged in - show login option (don't auto-redirect)
              setLoading(false);
            }
          } else {
            // Guest user - show lookup option (don't auto-redirect)
            setUserType('guest');
            setLoading(false);
          }
        } else {
          setError(response.data.error || 'Failed to check invoice redirect');
          setLoading(false);
        }
      } catch (err) {
        console.error('Error checking invoice redirect:', err);
        
        // On error, show both options and let user choose (no error message)
        setUserType(null); // Show both options
        setError(null); // Don't show error message
        setLoading(false);
      }
    };

    if (invoiceId) {
      checkRedirect();
    } else {
      setError('Invoice ID is missing');
      setLoading(false);
    }
  }, [invoiceId, navigate]);

  if (loading) {
    return (
      <>
        {seo}
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin text-[#1F11ED] mx-auto mb-4" />
            <p className="text-gray-600">Checking invoice...</p>
          </div>
        </div>
      </>
    );
  }

  // Show options based on user type or error
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      {seo}
      <div className="text-center max-w-md mx-auto px-4">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">View Invoice</h2>
        
        {/* Only show error if we have a specific user type (not when showing both options) */}
        {error && userType !== null && (
          <p className="text-sm text-red-600 mb-4">{error}</p>
        )}
        
        {userType === 'receevit' ? (
          <>
            <p className="text-gray-600 mb-6">
              This invoice is for a Receevit® user account. Please log in to view your invoice.
            </p>
            <button
              onClick={() => {
                const redirectUrl = `/portal/invoices/received?highlight=${invoiceId}`;
                navigate(`/login?redirect=${encodeURIComponent(redirectUrl)}`);
              }}
              className="w-full px-6 py-3 bg-[#1F11ED] text-white rounded-lg hover:bg-[#1508c9] font-medium"
            >
              Login to View Invoice
            </button>
          </>
        ) : userType === 'guest' ? (
          <>
            <p className="text-gray-600 mb-6">
              This is a guest invoice. Use the invoice lookup to view and pay.
            </p>
            <button
              onClick={() => {
                // Extract invoice number from URL or use lookup
                navigate(`/lookup?invoiceId=${invoiceId}`);
              }}
              className="w-full px-6 py-3 bg-[#1F11ED] text-white rounded-lg hover:bg-[#1508c9] font-medium"
            >
              Go to Invoice Lookup
            </button>
          </>
        ) : (
          <>
            <p className="text-gray-600 mb-6">
              Choose how you'd like to view this invoice:
            </p>
            <div className="space-y-3">
              <button
                onClick={() => {
                  const redirectUrl = `/portal/invoices/received?highlight=${invoiceId}`;
                  navigate(`/login?redirect=${encodeURIComponent(redirectUrl)}`);
                }}
                className="w-full px-6 py-3 bg-[#1F11ED] text-white rounded-lg hover:bg-[#1508c9] font-medium"
              >
                Login to View Invoice
              </button>
              <p className="text-sm text-gray-500">or</p>
              <button
                onClick={() => navigate(`/lookup?invoiceId=${invoiceId}`)}
                className="w-full px-6 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 font-medium"
              >
                Use Guest Invoice Lookup
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ViewInvoiceRedirect;

